import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

const LABELS = {
  en: 'English',
  de: 'German',
};

const ROLL_DURATION_MS = 480;

function RollingLabel({ label, rollTarget, rollPhase }) {
  const trackClassName = rollPhase === 'prepare'
    ? 'lang-switch-roll__track lang-switch-roll__track--prepare'
    : rollPhase === 'active'
      ? 'lang-switch-roll__track lang-switch-roll__track--active'
      : 'lang-switch-roll__track';

  return (
    <span className="lang-switch-roll">
      <span className={trackClassName}>
        {rollPhase && rollTarget ? (
          <span className="lang-switch-roll__item">{rollTarget}</span>
        ) : null}
        <span className="lang-switch-roll__item">{label}</span>
      </span>
    </span>
  );
}

export function LanguagePillSwitcher({ className = '' }) {
  const { language, toggleLanguage } = useLanguage();
  const theme = useTheme();
  const reducedMotion = usePrefersReducedMotion();
  const [rollPhase, setRollPhase] = useState(null);
  const rollFrameRef = useRef(null);

  const leftLabel = LABELS[language];
  const rightLabel = LABELS[language === 'en' ? 'de' : 'en'];
  const nextLeftLabel = LABELS[language === 'en' ? 'de' : 'en'];
  const nextRightLabel = LABELS[language];
  const targetLanguage = language === 'en' ? 'de' : 'en';

  const finishRoll = useCallback(() => {
    setRollPhase(null);
    toggleLanguage();
  }, [toggleLanguage]);

  useEffect(() => {
    if (rollPhase !== 'active') {
      return undefined;
    }

    const timeoutId = window.setTimeout(finishRoll, ROLL_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [rollPhase, finishRoll]);

  useEffect(
    () => () => {
      if (rollFrameRef.current !== null) {
        window.cancelAnimationFrame(rollFrameRef.current);
      }
    },
    []
  );

  const handleClick = () => {
    if (rollPhase) {
      return;
    }

    if (reducedMotion) {
      toggleLanguage();
      return;
    }

    setRollPhase('prepare');

    rollFrameRef.current = window.requestAnimationFrame(() => {
      rollFrameRef.current = window.requestAnimationFrame(() => {
        rollFrameRef.current = null;
        setRollPhase('active');
      });
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`lang-switcher ${theme.colors.text} ${className}`.trim()}
      aria-label={`Switch language to ${LABELS[targetLanguage]}`}
    >
      <RollingLabel label={leftLabel} rollTarget={nextLeftLabel} rollPhase={rollPhase} />
      <span className="lang-switcher__arrow" aria-hidden="true">
        →
      </span>
      <RollingLabel label={rightLabel} rollTarget={nextRightLabel} rollPhase={rollPhase} />
    </button>
  );
}
